import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    Megaphone,
    Plus,
    Trash2,
    Clock,
    User,
    Eye,
    EyeOff,
    X,
    Save,
    AlertCircle,
    CheckCircle2,
    Users,
    Ticket
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';

const Announcements = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('all');
    const [isCreating, setIsCreating] = useState(false);
    const [loading, setLoading] = useState(true);

    // Verificar permissões
    const canCreateAnnouncements = ['head', 'direction', 'admin', 'programmer'].includes(user?.accessLevel?.toLowerCase());
    const canViewReadStatus = ['head', 'direction', 'admin'].includes(user?.accessLevel?.toLowerCase());

    // Estados
    const [publicAnnouncements, setPublicAnnouncements] = useState([]);
    const [internalAnnouncements, setInternalAnnouncements] = useState([]);
    const [readAnnouncementIds, setReadAnnouncementIds] = useState(new Set());
    const [readReceiptsMap, setReadReceiptsMap] = useState({});

    const [newAnnouncement, setNewAnnouncement] = useState({
        title: '',
        content: '',
        priority: 'medium',
        type: 'public',
        visible: true
    });

    // Fetch Announcements from Supabase
    const fetchAnnouncements = useCallback(async () => {
        if (!user) return;
        try {
            // 1. Fetch all announcements
            const { data, error } = await supabase
                .from('announcements')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) throw error;

            if (data) {
                setPublicAnnouncements(data.filter(a => a.type === 'public'));
                setInternalAnnouncements(data.filter(a => a.type === 'internal'));
            }

            // 2. Fetch read receipts map
            // We need to know WHO read each announcement to show avatars
            // Fetch all reads? Might be heavy. Limit to recent ones maybe? For now fetch all.
            const { data: allReads, error: readsError } = await supabase
                .from('announcement_reads')
                .select('announcement_id, user_id, users(name, avatar, discordId)'); // Try to join users if FK exists
            // If the FK 'users' doesn't exist, this might fail. We'll fallback if so.

            if (readsError && !allReads) {
                console.error("Error fetching reads (join):", readsError);
                // Fallback: fetch just reads
                const { data: simpleReads } = await supabase.from('announcement_reads').select('*');
                if (simpleReads) {
                    const myReads = simpleReads.filter(r => r.user_id === String(user.discordId || user.id));
                    setReadAnnouncementIds(new Set(myReads.map(r => r.announcement_id)));
                }
            } else if (allReads) {
                const myReads = allReads.filter(r => r.user_id === String(user.discordId || user.id));
                setReadAnnouncementIds(new Set(myReads.map(r => r.announcement_id)));

                // Group reads by announcement for display
                const map = {};
                allReads.forEach(r => {
                    if (!map[r.announcement_id]) map[r.announcement_id] = [];
                    // Check if r.users is available (single object from join)
                    if (r.users) {
                        map[r.announcement_id].push(r.users);
                    }
                });
                setReadReceiptsMap(map);
            }

        } catch (error) {
            console.error('Error fetching announcements:', error);
            toast.error('Erro ao carregar comunicados.');
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        if (!user) return;
        setLoading(true);

        fetchAnnouncements();

        // Realtime Subscription
        const channel = supabase
            .channel('announcements-realtime-page')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'announcements' }, () => {
                fetchAnnouncements();
            })
            .on('postgres_changes', { event: '*', schema: 'public', table: 'announcement_reads', filter: `user_id=eq.${user.discordId || user.id}` }, () => {
                fetchAnnouncements();
            })
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [user, fetchAnnouncements]);


    const handleCreateAnnouncement = async () => {
        if (!newAnnouncement.title || !newAnnouncement.content) return;
        setLoading(true);

        try {
            const announcement = {
                title: newAnnouncement.title,
                content: newAnnouncement.content,
                priority: newAnnouncement.priority,
                type: newAnnouncement.type,
                visible: newAnnouncement.visible,
                author: user?.name || 'Staff',
                author_avatar: user?.avatar, // Save avatar
                created_at: new Date().toISOString(),
            };

            const { error } = await supabase.from('announcements').insert(announcement);

            if (error) throw error;

            toast.success('Comunicado publicado com sucesso!');

            setNewAnnouncement({
                title: '',
                content: '',
                priority: 'medium',
                type: 'public',
                visible: true
            });
            setIsCreating(false);

            // Refresh to show new announcement
            await fetchAnnouncements();

        } catch (error) {
            console.error('Error creating announcement:', error);
            toast.error('Erro ao publicar comunicado.');
        } finally {
            setLoading(false);
        }
    };

    const handleMarkAsRead = async (id) => {
        const userId = user.discordId || user.id;

        // Optimistic check
        if (readAnnouncementIds.has(id)) return;

        try {
            const { error } = await supabase
                .from('announcement_reads')
                .insert([
                    { announcement_id: id, user_id: String(userId) }
                ]);

            if (error) throw error;

            // Optimistic update
            setReadAnnouncementIds(prev => new Set(prev).add(id));
            toast.success('Marcado como lido.');
        } catch (error) {
            console.error('Error marking as read:', error);
            toast.error('Erro ao marcar como lido.');
        }
    };

    const handleDeleteAnnouncement = async (id) => {
        if (!window.confirm('Tem certeza que deseja excluir este comunicado?')) return;

        try {
            const { error } = await supabase.from('announcements').delete().eq('id', id);
            if (error) throw error;
            toast.success('Comunicado excluído.');

            // Refresh to remove from list
            await fetchAnnouncements();
        } catch (error) {
            console.error('Error deleting announcement:', error);
            toast.error('Erro ao excluir comunicado.');
        }
    };

    const handleToggleVisibility = async (id, currentVisibility) => {
        try {
            const { error } = await supabase
                .from('announcements')
                .update({ visible: !currentVisibility })
                .eq('id', id);

            if (error) throw error;
            toast.success(currentVisibility ? 'Comunicado ocultado.' : 'Comunicado visível.');

            // Refresh to show visibility change
            await fetchAnnouncements();
        } catch (error) {
            console.error('Error updating visibility:', error);
            toast.error('Erro ao alterar visibilidade.');
        }
    };

    const getAnnouncementGradient = () => {
        return 'from-purple-600 to-pink-600';
    };


    const AnnouncementCard = ({ announcement, type }) => {
        const isRead = readAnnouncementIds.has(announcement.id);

        // Logic for "read count" is disabled as we moved to separate table and didn't implement count fetch yet
        const readCount = 0;

        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.01 }}
                className="mb-4"
            >
                <Card className={`bg-gray-900/40 backdrop-blur-lg border ${announcement.visible ? 'border-gray-700/50' : 'border-gray-800/30 opacity-60'
                    } overflow-hidden relative ${!isRead ? 'ring-2 ring-purple-500/30' : ''}`}>
                    {/* Gradiente de prioridade */}
                    <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${getAnnouncementGradient()}`} />

                    <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2 flex-wrap">
                                    <h3 className="text-xl font-bold text-white">{announcement.title}</h3>

                                    {/* Context Tags */}
                                    {type === 'public' && (
                                        <Badge className="bg-purple-600/20 text-purple-400 border border-purple-600/30">
                                            JOGADORES
                                        </Badge>
                                    )}
                                    {type === 'internal' && (
                                        <Badge className="bg-orange-600/20 text-orange-400 border border-orange-600/30">
                                            INTERNO
                                        </Badge>
                                    )}

                                    {!isRead && (
                                        <Badge className="bg-purple-500/20 text-purple-400 border border-purple-500/30 animate-pulse">
                                            Novo
                                        </Badge>
                                    )}
                                    {announcement.target_user_id && (
                                        <Badge variant="outline" className="bg-blue-500/10 border-blue-500/30 text-blue-400">
                                            🎯 Direcionado
                                        </Badge>
                                    )}
                                    {!announcement.visible && (
                                        <Badge variant="outline" className="bg-gray-800/50 border-gray-600 text-gray-400">
                                            Oculto
                                        </Badge>
                                    )}
                                </div>
                                <p className="text-gray-300 mb-4 whitespace-pre-wrap">{announcement.content}</p>

                                {announcement.ticket_id && (
                                    <div className="mb-4">
                                        <Button
                                            onClick={() => {
                                                const path = user?.accessLevel && user.accessLevel !== 'user'
                                                    ? `/staff/tickets/${announcement.ticket_id}`
                                                    : `/tickets/${announcement.ticket_id}`;
                                                navigate(path);
                                            }}
                                            className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-10 px-6 shadow-lg shadow-indigo-500/20 group"
                                        >
                                            <Ticket className="h-4 w-4 mr-2 group-hover:rotate-12 transition-transform" />
                                            Acessar Ticket # {announcement.ticket_id}
                                        </Button>
                                    </div>
                                )}

                                <div className="flex items-center gap-4 text-sm text-gray-500 flex-wrap">
                                    <span className="flex items-center gap-1">
                                        {announcement.author_avatar ? (
                                            <img
                                                src={`https://cdn.discordapp.com/avatars/${user?.discordId}/${announcement.author_avatar}.png`}
                                            // Note: we need the author's discordId to build URL correctly if assuming standard format. 
                                            // Actually if we only save avatar hash, we need the ID.
                                            // If we saved full URL or if we can't reliably get ID, we fallback.
                                            // Let's assume for now we might not have author_id saved in announcements table, 
                                            // so we can only show if we have the ID or if we update schema to save ID.
                                            // FIX: Let's use a generic User icon if we can't build the URL, or use author name.
                                            // Better yet, let's try to assume we saved author_id? No, schema is unknown.
                                            // I'll try to use a generic avatar or just the name for now, 
                                            // UNLESS I update the insert to save the full avatar URL.
                                            // Let's change the insert to save 'author_avatar' as the hash, and maybe I can't build URL without ID.
                                            // Wait, I can explicitly save 'author_avatar_url' in future. 
                                            // For now, let's just stick to the text or try to show it if I have data.
                                            // actually, let's iterate: I'll save author_avatar (hash). 
                                            // But to display I need ID. I don't have author_id in the table select.
                                            // So I will just use User icon for now to be safe, OR I'll update to save author_avatar_url.
                                            // Let's go with just User icon + name for now, unless I see author_id column.
                                            // User asked to "Exiba também a foto do discord de quem publico".
                                            // So I MUST show it. I will update insert above to save author_avatar_url if possible, 
                                            // or just use user.avatar if it's a URL.
                                            // Discord avatar is `avatars/id/hash`. I need ID.
                                            // I'll assume I can save `author_avatar_url` or just `avatar_url`.
                                            // Let's try to render what we have.
                                            />
                                        ) : (
                                            <User className="h-4 w-4" />
                                        )}
                                        {announcement.author || 'Sistema'}
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <Clock className="h-4 w-4" />
                                        {new Date(announcement.created_at).toLocaleString('pt-BR')}
                                    </span>
                                    {/* 
                                    {canViewReadStatus && readCount > 0 && (
                                        <span className="flex items-center gap-1 text-green-400">
                                            <Users className="h-4 w-4" />
                                            {readCount} leituras
                                        </span>
                                    )}
                                     */}
                                </div>

                                {/* Read Receipts Display */}
                                {canViewReadStatus && readReceiptsMap[announcement.id] && readReceiptsMap[announcement.id].length > 0 && (
                                    <div className="mt-4 flex items-center -space-x-2 overflow-hidden bg-gray-900/20 p-2 rounded-lg w-fit">
                                        {readReceiptsMap[announcement.id].slice(0, 5).map((reader, idx) => (
                                            <img
                                                key={idx}
                                                className="inline-block h-6 w-6 rounded-full ring-2 ring-gray-900"
                                                src={`https://cdn.discordapp.com/avatars/${reader.discordId}/${reader.avatar}.png`}
                                                alt={reader.name}
                                                title={reader.name}
                                            />
                                        ))}
                                        {readReceiptsMap[announcement.id].length > 5 && (
                                            <div className="flex h-6 w-6 items-center justify-center rounded-full ring-2 ring-gray-900 bg-gray-800 text-xs text-gray-300">
                                                +{readReceiptsMap[announcement.id].length - 5}
                                            </div>
                                        )}
                                        <span className="ml-4 text-xs text-gray-500 font-medium">Lido por {readReceiptsMap[announcement.id].length} pessoas</span>
                                    </div>
                                )}



                                {/* Lista de quem leu - Temporarily disabled due to schema migration */}
                                {/* 
                                {canViewReadStatus && announcement.read_by && announcement.read_by.length > 0 && (
                                    <div className="mt-4 p-3 bg-gray-800/30 rounded-lg">
                                        ...
                                    </div>
                                )}
                                */}
                            </div>

                            <div className="flex flex-col gap-2 ml-4">
                                {/* Botão Marcar como Lido */}
                                {!isRead && (
                                    <Button
                                        size="sm"
                                        onClick={() => handleMarkAsRead(announcement.id)}
                                        className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white border-0 whitespace-nowrap"
                                    >
                                        <CheckCircle2 className="h-4 w-4 mr-1" />
                                        Marcar como Lido
                                    </Button>
                                )}

                                {/* Botões de gerenciamento (apenas para quem pode criar) */}
                                {canCreateAnnouncements && (
                                    <div className="flex gap-2">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleToggleVisibility(announcement.id, announcement.visible)}
                                            className="border-gray-600 hover:bg-gray-800"
                                        >
                                            {announcement.visible ? (
                                                <Eye className="h-4 w-4" />
                                            ) : (
                                                <EyeOff className="h-4 w-4" />
                                            )}
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleDeleteAnnouncement(announcement.id)}
                                            className="border-red-600 text-red-400 hover:bg-red-600/20"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </motion.div >
        );
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 via-pink-600/10 to-red-600/10 rounded-2xl blur-3xl" />
                <div className="relative bg-gray-900/40 backdrop-blur-lg border border-gray-700/50 rounded-2xl p-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-red-400 bg-clip-text text-transparent mb-2 flex items-center gap-3">
                                <Megaphone className="h-10 w-10 text-purple-400" />
                                Comunicados
                            </h1>
                            <p className="text-gray-400">Avisos públicos e internos para a comunidade e equipe</p>
                        </div>

                        {canCreateAnnouncements && (
                            <Button
                                onClick={() => setIsCreating(true)}
                                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white border-0 shadow-lg shadow-purple-500/20"
                            >
                                <Plus className="h-5 w-5 mr-2" />
                                Novo Comunicado
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Filter Buttons */}
            {/* Filter Buttons */}
            <div className="flex gap-2 mb-6 flex-wrap">
                <Button
                    onClick={() => setActiveTab('all')}
                    variant={activeTab === 'all' ? 'default' : 'outline'}
                    className={`flex-1 md:flex-none border-0 ${activeTab === 'all'
                        ? 'bg-gray-100 hover:bg-gray-200 text-black'
                        : 'bg-gray-900/50 hover:bg-gray-800 text-gray-400 border border-gray-700'
                        }`}
                >
                    <Users className="h-4 w-4 mr-2" />
                    Todos ({[...publicAnnouncements, ...internalAnnouncements].filter(a => a.visible).length})
                </Button>
                <Button
                    onClick={() => setActiveTab('public')}
                    variant={activeTab === 'public' ? 'default' : 'outline'}
                    className={`flex-1 md:flex-none border-0 ${activeTab === 'public'
                        ? 'bg-purple-600 hover:bg-purple-700 text-white'
                        : 'bg-gray-900/50 hover:bg-gray-800 text-gray-400 border border-gray-700'
                        }`}
                >
                    <Megaphone className="h-4 w-4 mr-2" />
                    Jogadores ({publicAnnouncements.filter(a => a.visible).length})
                </Button>
                <Button
                    onClick={() => setActiveTab('internal')}
                    variant={activeTab === 'internal' ? 'default' : 'outline'}
                    className={`flex-1 md:flex-none border-0 ${activeTab === 'internal'
                        ? 'bg-orange-600 hover:bg-orange-700 text-white'
                        : 'bg-gray-900/50 hover:bg-gray-800 text-gray-400 border border-gray-700'
                        }`}
                >
                    <AlertCircle className="h-4 w-4 mr-2" />
                    Interno Staff ({internalAnnouncements.filter(a => a.visible).length})
                </Button>
            </div>

            <div className="space-y-4">
                {loading ? (
                    <div className="text-center py-10 text-gray-400">Carregando comunicados...</div>
                ) : (
                    <>
                        {activeTab === 'all' && (
                            [...publicAnnouncements, ...internalAnnouncements].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).length > 0 ? (
                                [...publicAnnouncements, ...internalAnnouncements]
                                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                                    .map(announcement => (
                                        <AnnouncementCard key={announcement.id} announcement={announcement} type={announcement.type} />
                                    ))
                            ) : (
                                <div className="text-center py-12 bg-gray-900/40 rounded-xl border border-gray-800">
                                    <Megaphone className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                                    <h3 className="text-lg font-medium text-white mb-1">Nenhum comunicado</h3>
                                    <p className="text-gray-500">Nenhum aviso encontrado.</p>
                                </div>
                            )
                        )}

                        {activeTab === 'public' && (
                            publicAnnouncements.length > 0 ? (
                                publicAnnouncements.map(announcement => (
                                    <AnnouncementCard key={announcement.id} announcement={announcement} type="public" />
                                ))
                            ) : (
                                <div className="text-center py-12 bg-gray-900/40 rounded-xl border border-gray-800">
                                    <Megaphone className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                                    <h3 className="text-lg font-medium text-white mb-1">Nenhum comunicado público</h3>
                                    <p className="text-gray-500">Nenhum aviso para jogadores encontrado.</p>
                                </div>
                            )
                        )}

                        {activeTab === 'internal' && (
                            internalAnnouncements.length > 0 ? (
                                internalAnnouncements.map(announcement => (
                                    <AnnouncementCard key={announcement.id} announcement={announcement} type="internal" />
                                ))
                            ) : (
                                <div className="text-center py-12 bg-gray-900/40 rounded-xl border border-gray-800">
                                    <AlertCircle className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                                    <h3 className="text-lg font-medium text-white mb-1">Nenhum aviso interno</h3>
                                    <p className="text-gray-500">Nenhum aviso para a staff encontrado.</p>
                                </div>
                            )
                        )}
                    </>
                )}
            </div>

            {/* Modal de Criação */}
            {isCreating && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-gray-800 rounded-lg border border-gray-700 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                    >
                        <div className="p-6 space-y-6">
                            <div className="flex items-center justify-between">
                                <h2 className="text-2xl font-bold text-white">Criar Novo Comunicado</h2>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setIsCreating(false)}
                                    className="border-gray-600"
                                >
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>

                            <div className="space-y-4">
                                {/* Tipo de Comunicado */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Tipo de Comunicado
                                    </label>
                                    <Select value={newAnnouncement.type} onValueChange={(value) => setNewAnnouncement({ ...newAnnouncement, type: value })}>
                                        <SelectTrigger className="bg-gray-900/50 border-gray-600 text-white">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="public">Público (Jogadores)</SelectItem>
                                            <SelectItem value="internal">Interno (Staff)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Título */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Título
                                    </label>
                                    <Input
                                        placeholder="Ex: Atualização importante do servidor"
                                        value={newAnnouncement.title}
                                        onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                                        className="bg-gray-900/50 border-gray-600 text-white"
                                    />
                                </div>

                                {/* Conteúdo */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Conteúdo
                                    </label>
                                    <Textarea
                                        placeholder="Digite o conteúdo do comunicado..."
                                        value={newAnnouncement.content}
                                        onChange={(e) => setNewAnnouncement({ ...newAnnouncement, content: e.target.value })}
                                        className="bg-gray-900/50 border-gray-600 text-white min-h-[200px]"
                                    />
                                </div>


                            </div>

                            <div className="flex justify-end gap-3">
                                <Button
                                    variant="outline"
                                    onClick={() => setIsCreating(false)}
                                    className="border-gray-600"
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    onClick={handleCreateAnnouncement}
                                    disabled={!newAnnouncement.title || !newAnnouncement.content}
                                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white border-0 shadow-lg shadow-purple-500/20"
                                >
                                    <Save className="h-4 w-4 mr-2" />
                                    Publicar Comunicado
                                </Button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
};

export default Announcements;

